<script lang="ts">
  // Pill-shaped chip: quick picks, presets, active filters, recent clients.
  //   <Chip label="15 min" selected={v === 15} onclick={() => (v = 15)} />     toggle (aria-pressed)
  //   <Chip label="Acme" color={clientColor(c)} tint onclick={start} />         client-coloured
  //   <Chip label="Klant: Acme" onremove={() => clear('client')} />             removable filter
  //   <Chip label="Help" href="/app/support" icon={LifeBuoy} />                 link
  // Group several in a <ChipGroup>.
  import type { Component, Snippet } from 'svelte';
  import { X } from '@lucide/svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';
  import ColorDot from './ColorDot.svelte';

  interface Props {
    label?: string;
    children?: Snippet;
    /** Toggle state; when set the chip is a toggle button (aria-pressed). */
    selected?: boolean;
    onclick?: (event: MouseEvent) => void;
    href?: string;
    /** Shows a remove (×) button. */
    onremove?: () => void;
    /** Colour dot before the label (client, series). */
    color?: string;
    /** Tint the chip with `color` instead of showing a dot. */
    tint?: boolean;
    icon?: Component<any>;
    /** Extra text at the end, e.g. a count or duration. */
    meta?: string;
    size?: 'sm' | 'md';
    variant?: 'outline' | 'soft';
    disabled?: boolean;
    title?: string;
    ariaLabel?: string;
    class?: string;
  }
  let { label, children, selected, onclick, href, onremove, color, tint = false, icon: Icon, meta, size = 'md', variant = 'outline', disabled = false, title, ariaLabel, class: cls = '' }: Props = $props();

  const interactive = $derived(!!onclick || !!href);
  const name = $derived(ariaLabel || label || '');
</script>

{#snippet body()}
  {#if color && !tint}<ColorDot {color} size="sm" />{/if}
  {#if Icon}<Icon aria-hidden="true" />{/if}
  <span class="lbl">{#if children}{@render children()}{:else}{label}{/if}</span>
  {#if meta}<span class="meta tabular">{meta}</span>{/if}
{/snippet}

<span
  class="chip {size} {variant} {cls}"
  class:selected={selected === true}
  class:interactive
  class:tint={tint && !!color}
  class:removable={!!onremove}
  class:disabled
  style:--c={color}
  {title}
>
  {#if href}
    <a class="main" href={disabled ? undefined : href} aria-label={ariaLabel} aria-disabled={disabled || undefined}>{@render body()}</a>
  {:else if onclick}
    <button type="button" class="main" {disabled} aria-label={ariaLabel} aria-pressed={selected === undefined ? undefined : selected} {onclick}>{@render body()}</button>
  {:else}
    <span class="main">{@render body()}</span>
  {/if}
  {#if onremove}
    <button type="button" class="x" {disabled} aria-label={t('ui.remove', { name })} onclick={(e) => { e.stopPropagation(); onremove?.(); }}><X /></button>
  {/if}
</span>

<style>
  .chip {
    --chip-h: var(--control-h-sm);
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    height: var(--chip-h);
    border: 1px solid var(--border);
    border-radius: var(--radius-pill);
    background: var(--surface);
    color: var(--text);
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    transition: border-color var(--dur), background var(--dur), color var(--dur), box-shadow var(--dur);
    flex-shrink: 0;
  }
  .sm { --chip-h: 26px; font-size: var(--text-xs); }
  .soft { background: var(--surface-3); border-color: transparent; color: var(--text-2); }
  .main {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1_5);
    height: 100%;
    min-width: 0;
    padding: 0 var(--space-3);
    border: 0;
    border-radius: inherit;
    background: none;
    color: inherit;
    font: inherit;
    text-decoration: none;
  }
  .sm .main { padding: 0 var(--space-2_5); gap: var(--space-1); }
  .removable .main { padding-right: var(--space-1); }
  .main :global(svg) { width: var(--icon-sm); height: var(--icon-sm); color: var(--text-3); }
  .lbl { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { color: var(--text-3); font-weight: var(--weight-medium); }
  a.main:hover { text-decoration: none; }
  .main:focus-visible { outline: none; box-shadow: var(--ring); }

  .interactive:hover:not(.disabled) { border-color: var(--border-strong); background: var(--surface-hover); }
  .soft.interactive:hover:not(.disabled) { border-color: transparent; color: var(--text); }
  .selected, .selected.interactive:hover { background: var(--brand-soft); border-color: color-mix(in srgb, var(--brand) 45%, transparent); color: var(--brand); }
  .selected .main :global(svg), .selected .meta { color: var(--brand); }

  .tint { background: color-mix(in srgb, var(--c) 10%, var(--surface)); border-color: color-mix(in srgb, var(--c) 28%, var(--border)); }
  .tint .main :global(svg) { color: var(--c); }
  .tint.interactive:hover:not(.disabled) { background: color-mix(in srgb, var(--c) 16%, var(--surface)); border-color: color-mix(in srgb, var(--c) 45%, var(--border)); }
  .tint.selected { box-shadow: 0 0 0 1px var(--c); color: var(--text); }

  .disabled { opacity: 0.5; }
  .disabled .main { cursor: not-allowed; }

  .x {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    margin-right: var(--space-1);
    padding: 0;
    border: 0;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--text-3);
    flex-shrink: 0;
  }
  .x :global(svg) { width: var(--icon-xs); height: var(--icon-xs); }
  .x:hover { background: color-mix(in srgb, currentColor 12%, transparent); color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }
</style>
